import axios from 'axios';
import { config } from '../config';
import SessionService from './SessionService';

const API_URL = config.API_BASE_URL;

export interface HoursDay {
  /** 0 = domingo … 6 = sábado. */
  day: number;
  open: string;
  close: string;
}

export interface SlaRule {
  id: number;
  sectorId: number;
  name: string;
  enabled: boolean;
  scopeKind: string;
  scopeValue: string | null;
  deadlineMinutes: number;
  aiMayReply: boolean;
  escalateToUserId: number | null;
  enabledAtUtc: string;
}

export interface SlaRuleWrite {
  sectorId: number;
  name: string;
  enabled: boolean;
  scopeKind: string;
  scopeValue: string | null;
  deadlineMinutes: number;
  aiMayReply: boolean;
  escalateToUserId: number | null;
}

export interface SlaStatusRow {
  contactId: number;
  contactName: string;
  waitingSinceUtc: string;
  ruleName: string | null;
  deadlineMinutes: number | null;
  elapsedMinutes: number;
  status: string;
  label: string;
  reason: string | null;
  episodeId: number | null;
}

export interface AttendanceOption {
  value: string;
  label: string;
}

export interface AttendanceOptions {
  channels: AttendanceOption[];
  origins: AttendanceOption[];
  forms: AttendanceOption[];
  campaigns: AttendanceOption[];
  users: { id: number; name: string }[];
  agents: { id: number; name: string; enabled: boolean; capReceptive: boolean; capCampaign: boolean }[];
}

export interface SlaConfig {
  settings: { businessHours: HoursDay[] | null };
  rules: SlaRule[];
}

function authHeaders() {
  return {
    Accept: 'application/json',
    Authorization: `Bearer ${SessionService.getToken()}`,
  };
}

function errorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return fallback;
}

export async function getSla(sectorId: number): Promise<SlaConfig> {
  try {
    const response = await axios.get(`${API_URL}/attendance/sla`, { params: { sectorId }, headers: authHeaders() });
    const data = response.data?.data;
    return { settings: { businessHours: data?.settings?.businessHours ?? null }, rules: data?.rules ?? [] };
  } catch (error) {
    throw new Error(errorMessage(error, 'Não foi possível carregar o prazo de atendimento.'));
  }
}

export async function saveSlaSettings(sectorId: number, businessHours: HoursDay[] | null): Promise<void> {
  try {
    await axios.put(`${API_URL}/attendance/sla/settings`, { sectorId, businessHours }, { headers: authHeaders() });
  } catch (error) {
    throw new Error(errorMessage(error, 'Não foi possível salvar o horário.'));
  }
}

export async function createSlaRule(input: SlaRuleWrite): Promise<void> {
  try {
    await axios.post(`${API_URL}/attendance/sla/rules`, input, { headers: authHeaders() });
  } catch (error) {
    throw new Error(errorMessage(error, 'Não foi possível salvar a regra.'));
  }
}

export async function updateSlaRule(id: number, input: SlaRuleWrite): Promise<void> {
  try {
    await axios.put(`${API_URL}/attendance/sla/rules/${id}`, input, { headers: authHeaders() });
  } catch (error) {
    throw new Error(errorMessage(error, 'Não foi possível salvar a regra.'));
  }
}

export async function deleteSlaRule(id: number, sectorId: number): Promise<void> {
  try {
    await axios.delete(`${API_URL}/attendance/sla/rules/${id}`, { params: { sectorId }, headers: authHeaders() });
  } catch (error) {
    throw new Error(errorMessage(error, 'Não foi possível excluir a regra.'));
  }
}

export async function getSlaStatus(sectorId: number): Promise<SlaStatusRow[]> {
  try {
    const response = await axios.get(`${API_URL}/attendance/sla/status`, { params: { sectorId }, headers: authHeaders() });
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(errorMessage(error, 'Não foi possível carregar a situação das conversas.'));
  }
}

export async function getAttendanceOptions(sectorId: number): Promise<AttendanceOptions> {
  try {
    const response = await axios.get(`${API_URL}/attendance/options`, { params: { sectorId }, headers: authHeaders() });
    const data = response.data?.data;
    return {
      channels: data?.channels ?? [],
      origins: data?.origins ?? [],
      forms: data?.forms ?? [],
      campaigns: data?.campaigns ?? [],
      users: data?.users ?? [],
      agents: data?.agents ?? [],
    };
  } catch (error) {
    throw new Error(errorMessage(error, 'Não foi possível carregar as opções.'));
  }
}
